import { faEnvelope, faLocationDot, faPhone } from "@fortawesome/free-solid-svg-icons";
import livingRoomImg from "../assets/images/shutterstock_2775581045.jpg"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useEffect, useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { useLocation } from "react-router-dom";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const location = useLocation();
  const [popup, setPopup] = useState(false);
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
  } = useUserParams();
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const ContactPage = () => {
    return (
      <section className="relative overflow-hidden">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div
            variants={fadeUp}
            className="grid lg:grid-cols-12"
          >
            {/* CONTACT SIDE */}
            <div className="order-2 lg:order-1 lg:col-span-6 bg-light px-6 py-20 sm:px-8 md:px-12 lg:py-40 lg:pl-24 xl:pl-48">
              {/* HEADER */}
              <div className="mb-20">
                <motion.p
                  variants={fadeUp}
                  className="mb-6 text-sm uppercase tracking-[0.6em] text-black/40"
                >
                  Get In Touch
                </motion.p>

                <motion.h2
                  variants={fadeUp}
                  className="max-w-4xl font-heading2 text-4xl leading-tight text-neutral-800 sm:text-5xl lg:text-6xl lg:leading-[1.05]"
                >
                  Your Next Chapter
                  <br />
                  Begins Here
                </motion.h2>
                <div className="mt-10 h-px w-24 bg-accent" />
              </div>
              <div className="border-l-2 border-accent/60 pl-8">
                <h3 className="font-serif text-3xl sm:text-4xl text-neutral-800">
                  {getCurrentAgentName()}
                </h3>

                <p className="mt-4 text-gray-700 leading-8">
                  {getCurrentAgentTitle()}
                  <br />
                  {getCurrentBrokerage()}
                </p>
              </div>

              <div className="mt-10 space-y-6 lg:mt-12 lg:space-y-8">
                <div className="group">
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Office
                  </p>

                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="text-accent"
                    />

                    <p className="leading-7 text-gray-600">
                      {constants.AGENT_ADDRESS}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Phone
                  </p>

                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-accent"
                    />

                    <p className="text-gray-600">
                      {getCurrentAgentNumber()}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Email
                  </p>
                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-accent"
                    />

                    <p className="text-gray-600">
                      {getCurrentAgentEmail()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="max-w-xl mt-10 border-t border-accent/50 pt-8">
                <p className="text-sm leading-8 text-gray-600">
                  Thoughtful advice, strategic guidance, and personalized service
                  for every stage of your real estate journey.
                </p>
              </div>
            </div>

            {/* image */}
            <div className="order-1 lg:order-2 lg:col-span-6">
              <img
                src={livingRoomImg}
                alt=""
                className="h-72 w-full object-cover sm:h-96 lg:h-full lg:pt-24"
              />
            </div>
          </motion.div>

          {/* FORM */}
          <motion.div
            variants={fadeUp}
            className="mx-auto max-w-4xl px-6 py-20 sm:px-8 md:px-12 lg:px-16 lg:py-32">
            <p
              className="text-sm uppercase tracking-[0.6em] text-accent mb-8 text-center"
            >
              We are here to help
            </p>
            <h3 className="mb-16 text-center font-heading2 text-4xl text-neutral-800 sm:text-5xl lg:mb-20 lg:text-6xl">
              Get in Touch
            </h3>

            <form
              onSubmit={submitForm}
            >
              <div className="grid md:grid-cols-2 gap-10 mb-10">
                <div>
                  <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                    Name*
                  </label>

                  <input
                    name="Name"
                    value={formData.Name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    className="w-full border border-gray-200 p-4 outline-none focus:border-black/50"
                    required
                  />
                </div>

                <div>
                  <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                    Email*
                  </label>

                  <input
                    name="Email Address"
                    value={formData["Email Address"]}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className="w-full border border-gray-200 p-4 outline-none focus:border-black/50"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                  Message*
                </label>

                <textarea
                  rows={5}
                  name="Message"
                  value={formData.Message}
                  onChange={handleInputChange}
                  placeholder="How can we help?"
                  className="w-full border border-gray-200 p-4 outline-none focus:border-black/50"
                  required
                />
              </div>

              <div className="w-full flex justify-center mt-16">
                <button
                  type="submit"
                  className="btn-primary-outline-accent"
                >
                  Submit Inquiry
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </section>
    )
  }

  const ContactSection = () => {
    return (
      <section className="relative overflow-hidden bg-light/50 py-32">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1400px] px-6 lg:px-16"
        >
          {/* HEADER */}
          <div className="mb-20">
            <motion.p
              variants={fadeUp}
              className="mb-6 text-sm uppercase tracking-[0.6em] text-black/40"
            >
              Get In Touch
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="max-w-4xl font-heading2 text-5xl lg:text-6xl leading-[1.05] text-neutral-800"
            >
              Your Next Chapter
              <br />
              Begins Here
            </motion.h2>
            <div className="mt-10 h-px w-24 bg-accent" />
          </div>

          <motion.div
            variants={fadeUp}
            className="grid lg:grid-cols-12 gap-16"
          >

            {/* CONTACT SIDE */}
            <div className="lg:col-span-5">
              <div className="border-l-2 border-accent/60 pl-8">
                <h3 className="font-serif text-4xl text-neutral-800">
                  {getCurrentAgentName()}
                </h3>

                <p className="mt-4 text-gray-700 leading-8">
                  {getCurrentAgentTitle()}
                  <br />
                  {getCurrentBrokerage()}
                </p>
              </div>

              <div className="mt-12 space-y-8">
                <div className="group">
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Office
                  </p>

                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="text-accent"
                    />

                    <p className="leading-7 text-gray-600">
                      {constants.AGENT_ADDRESS}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Phone
                  </p>

                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-accent"
                    />

                    <p className="text-gray-600">
                      {getCurrentAgentNumber()}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-black/40">
                    Email
                  </p>
                  <div className="mt-3 flex gap-4 items-center">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-accent"
                    />

                    <p className="text-gray-600">
                      {getCurrentAgentEmail()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 border-t border-accent/50 pt-8">
                <p className="text-sm leading-8 text-gray-600">
                  Thoughtful advice, strategic guidance, and personalized service for every stage of your real estate journey.
                </p>
              </div>
            </div>

            {/* FORM */}
            <div className="lg:col-span-7">
              <div
                className="border border-accent/40 bg-white p-8 lg:p-14"
              >
                <h3 className="font-serif text-4xl text-neutral-800 mb-12">
                  Send A Message
                </h3>

                <form
                  onSubmit={submitForm}
                  className="space-y-10"
                >
                  <div className="grid md:grid-cols-2 gap-10">
                    <div>
                      <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                        Name
                      </label>

                      <input
                        name="Name"
                        value={formData.Name}
                        onChange={handleInputChange}
                        placeholder="Your Name"
                        className="w-full border-b border-gray-300 py-4 outline-none focus:border-black/50"
                      />
                    </div>

                    <div>
                      <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                        Email
                      </label>

                      <input
                        name="Email Address"
                        value={formData["Email Address"]}
                        onChange={handleInputChange}
                        placeholder="Email Address"
                        className="w-full border-b border-gray-300 py-4 outline-none focus:border-black/50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-3 block text-xs uppercase tracking-[0.35em] text-black/40">
                      Message
                    </label>

                    <textarea
                      rows={5}
                      name="Message"
                      value={formData.Message}
                      onChange={handleInputChange}
                      placeholder="How can we help?"
                      className="w-full border-b border-gray-300 py-4 outline-none focus:border-black/50"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary-outline-accent"
                  >
                    Submit Inquiry
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>
    )
  }

  const seoConfig = pageSEO.contact();
  return (
    <>
      <SEO {...seoConfig} />

      {location.pathname === '/contact'
        ?
        ContactPage()
        :
        ContactSection()
      }

      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-lg -translate-x-1/2 -translate-y-1/2 border border-[#d8d0c6] bg-[#fffdfa] p-12 text-center shadow-2xl">
            <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full border border-[#8b8172] text-2xl text-[#8b8172]">
              ✓
            </div>

            <h3 className="font-serif text-4xl text-[#2b2925]">
              {popup.subject}
            </h3>

            <p className="mt-6 leading-8 text-[#5f594f]">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-10 inline-flex border border-[#8b8172] px-10 py-4 text-xs uppercase tracking-[0.35em] text-[#2b2925] transition-all duration-300 hover:bg-[#2b2925] hover:text-[#eee9df]"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
